import { useState, useEffect } from "react";
import { Share2, MessageCircle, MessageSquare, Mail, Copy, Check, Send, Sparkles } from "lucide-react";
import { useCountry } from "../context/CountryContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * ShareButtons — boutons de partage adaptés à la juridiction active.
 *
 * Génère automatiquement un lien avec le bon préfixe pays (/be, /fr, /lu, /ch)
 * et propose : WhatsApp, SMS, Email, Copier le lien.
 *
 * Tente Web Share API natif d'abord (iOS/Android) → fallback boutons individuels.
 *
 * Variant:
 *   - "inline" : boutons en ligne, sobre (utilisé sur Landing)
 *   - "card"   : grosse carte mise en avant
 */

const COUNTRY_TO_SLUG = { "be": "be", "fr": "fr", "lu": "lu", "ch-fr": "ch" };

const COUNTRY_INTRO = {
  "be": "Je viens de découvrir Un Avocat en Or : assistant juridique IA conforme au droit belge, en cas de litige ou d'arnaque.",
  "fr": "Je viens de découvrir Un Avocat en Or : assistant juridique IA conforme au droit français, en cas de litige ou d'arnaque.",
  "lu": "Je viens de découvrir Un Avocat en Or : assistant juridique IA adapté au droit luxembourgeois.",
  "ch-fr": "Je viens de découvrir Un Avocat en Or : assistant juridique IA adapté au droit suisse.",
};

export default function ShareButtons({ variant = "inline", className = "" }) {
  const { country } = useCountry();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);

  // Charge le compteur "amis aidés" si connecté
  useEffect(() => {
    if (!user) { setStats(null); return; }
    api.get("/referrals/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [user]);

  const slug = COUNTRY_TO_SLUG[country.id] || "";
  const refSuffix = user ? `?r=${user.user_id}` : "";
  const url = `${window.location.origin}${slug ? "/" + slug : ""}${refSuffix}`;
  const intro = COUNTRY_INTRO[country.id] || COUNTRY_INTRO["be"];
  const text = `${intro} ${url}`;

  const handleNativeShare = async () => {
    if (!navigator.share) {
      // Fallback : copier le lien
      handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: "Un Avocat en Or",
        text: intro,
        url,
      });
    } catch (e) {
      // L'utilisateur a annulé — pas d'erreur à afficher
      if (e.name !== "AbortError") console.warn("Share failed:", e);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier — sélectionnez le lien manuellement");
    }
  };

  // URLs de partage (s'ouvrent dans un nouvel onglet ou app native)
  const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
  const smsUrl = `sms:?body=${encodeURIComponent(text)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent("Un Avocat en Or — assistant juridique en 5s")}&body=${encodeURIComponent(text)}`;
  const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(intro)}`;

  if (variant === "card") {
    return (
      <div
        className={`bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-sm p-6 sm:p-8 ${className}`}
        data-testid="share-card"
      >
        <div className="flex items-center gap-3 mb-3">
          <Share2 className="h-5 w-5 text-[#B89C65]" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65]">Aidez un proche</p>
        </div>
        <h3 className="font-serif-display text-2xl sm:text-3xl font-medium mb-2">
          Partagez {country.flag} {country.name}
        </h3>
        <p className="text-sm text-slate-300 mb-5 leading-relaxed">
          Un proche pourrait avoir besoin de cette app. Envoyez-lui le lien direct :
          il arrivera automatiquement en mode {country.name}.
        </p>

        <div className="bg-slate-950 border border-slate-700 rounded-sm px-3 py-2 mb-5 flex items-center gap-2" data-testid="share-link-preview">
          <p className="text-xs text-slate-400 font-mono truncate flex-1">{url}</p>
          <button
            type="button"
            onClick={handleCopy}
            className="text-slate-300 hover:text-white flex-shrink-0"
            aria-label="Copier le lien"
            data-testid="share-copy-button"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>

        {/* Compteur viral — uniquement pour utilisateurs connectés */}
        {user && stats && (stats.visits > 0 || stats.signups > 0) && (
          <div className="mb-5 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-sm p-4 flex items-center gap-3" data-testid="referral-counter">
            <Sparkles className="h-5 w-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-serif-display text-lg font-medium text-white leading-tight">
                {stats.signups > 0 ? (
                  <>
                    <span className="text-amber-400">{stats.signups}</span>{" "}
                    {stats.signups === 1 ? "ami aidé" : "amis aidés"} grâce à vous
                  </>
                ) : (
                  <>
                    <span className="text-amber-400">{stats.visits}</span>{" "}
                    {stats.visits === 1 ? "visite" : "visites"} via votre lien
                  </>
                )}
              </p>
              {stats.signups > 0 && stats.visits > stats.signups && (
                <p className="text-xs text-slate-400 mt-0.5">
                  + {stats.visits - stats.signups} {(stats.visits - stats.signups) === 1 ? "personne" : "personnes"} en train de découvrir
                </p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <ShareLink href={waUrl} icon={MessageCircle} label="WhatsApp" color="bg-emerald-600 hover:bg-emerald-700" testid="share-whatsapp" />
          <ShareLink href={smsUrl} icon={MessageSquare} label="SMS" color="bg-blue-600 hover:bg-blue-700" testid="share-sms" external={false} />
          <ShareLink href={emailUrl} icon={Mail} label="Email" color="bg-slate-600 hover:bg-slate-700" testid="share-email" external={false} />
          <ShareLink href={tgUrl} icon={Send} label="Telegram" color="bg-sky-600 hover:bg-sky-700" testid="share-telegram" />
        </div>

        {navigator.share && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="mt-3 w-full bg-[#B89C65] hover:bg-[#A38A58] text-white rounded-sm py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            data-testid="share-native-button"
          >
            <Share2 className="h-4 w-4" />
            Plus d'options de partage
          </button>
        )}
      </div>
    );
  }

  // Variant "inline" — boutons sobres en ligne
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="share-inline">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mr-1">Partager {country.flag} :</p>
      <ShareLink href={waUrl} icon={MessageCircle} label="WhatsApp" color="bg-emerald-600 hover:bg-emerald-700" testid="share-whatsapp" size="sm" />
      <ShareLink href={smsUrl} icon={MessageSquare} label="SMS" color="bg-blue-600 hover:bg-blue-700" testid="share-sms" size="sm" external={false} />
      <ShareLink href={emailUrl} icon={Mail} label="Email" color="bg-slate-600 hover:bg-slate-700" testid="share-email" size="sm" external={false} />
      <button
        type="button"
        onClick={handleCopy}
        className={`inline-flex items-center gap-1.5 rounded-sm bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-medium transition-colors`}
        data-testid="share-copy-button"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copié" : "Copier"}
      </button>
    </div>
  );
}

function ShareLink({ href, icon: Icon, label, color, testid, size = "md", external = true }) {
  const sizeCls = size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-3 text-sm";
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className={`inline-flex items-center justify-center gap-1.5 rounded-sm ${color} text-white ${sizeCls} font-medium transition-colors`}
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </a>
  );
}
